/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

/**
 * Utility pro operace s Promise.
 */
class PromiseUtil {
  /**
   * Funguje stejně jako standardní `Promise.all`, ale předávané pole je
   * ve stejném formátu jako {@link PromiseUtil.allSerial}, tj. pole továren
   * na Promise.
   *
   * Slouží zejména pro rychlé přepnutí allSerial varianty na paralelní.
   *
   * Nedostatky a nepodporované funkcionality:
   * - Vstup by měl být Iterable, ale podporováno je zatím jen Array.
   * - Vyžaduje se, aby funkce vracely Promise.
   * - Pravděpodobně i další, nebylo implementováno do detailu dle specifikace.
   *
   * Zdroje:
   * - [MDN Promise.all](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all)
   * @template {readonly (() => Promise<unknown>)[] | []} T
   * @param {T} array
   * @returns {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>}
   */
  static allParallel(array) {
    return /** @type {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>} */ (
      Promise.all(array.map((fn) => fn()))
    );
  }

  /**
   * Funguje podobně jako standardní `Promise.all`, akorát se jednotlivé Promise
   * volají sériově a nikoliv paralelně.
   *
   * Nedostatky a nepodporované funkcionality:
   * - Vstup by měl být Iterable, ale podporováno je zatím jen Array.
   * - Vyžaduje se, aby funkce vracely Promise.
   * - Pravděpodobně i další, nebylo implementováno do detailu dle specifikace.
   *
   * Zdroje:
   * - [MDN Promise.all](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all)
   * @template {readonly (() => Promise<unknown>)[] | []} T
   * @param {T} array
   * @returns {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>}
   */
  static allSerial(array) {
    return new Promise((resolve) =>
      resolve(
        array.reduce(
          (acc, fn, i) =>
            acc.then((results) =>
              fn().then((result) => {
                results[i] = result;
                return results;
              })
            ),
          /** @type {Promise<{ -readonly [P in keyof T]: Awaited<ReturnType<T[P]>> }>} */
          (Promise.resolve(Array(array.length)))
        )
      )
    );
  }

  /**
   * Volá opakovaně asynchronní metodu dokud neuspěje.
   * Po každém neúspěšném pokusu se aplikuje zpožení.
   * @template T
   * @param {number} delay Čas po který se čeká,
   * po každém neúspěšném pokusu. [ms]
   * @param {number} max Maximání počet neúspěšných pokusů.
   * @param {() => Promise<T>} operation Asynchronní operace, která se opakuje.
   * @returns {Promise<T | void>}
   */
  static async retry(delay, max, operation) {
    let attempts = 0;

    while (attempts < max) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await operation();
      } catch (error) {
        attempts += 1;
        if (attempts >= max) {
          throw error;
        }
        // eslint-disable-next-line no-await-in-loop
        await PromiseUtil.wait(delay);
      }
    }
  }

  /**
   * Počká dokud neuplyne pořadovaný čas.
   * @param {number} [delay] Čas po který se čeká, než se Promise vyřeší. [ms]
   * @returns {Promise<void>}
   */
  static wait(delay) {
    return new Promise((resolve) => setTimeout(resolve, delay));
  }
}

export default PromiseUtil;
